import { parse } from './urlHelpers';
import { SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID } from '../config/primaryEventDiscovery';
import { parseSelectFilterOptions } from './search';
import { sortPrimaryEventsForSearch } from './sortPrimaryEventsForSearch';
import {
  EVENT_DATES_QUERY_PARAM,
  primaryEventHasEnded,
  primaryEventMatchesEventDatesFilter,
} from './eventDateRangeFilter';

/** PromoTix API V2: `event_privacy_type` — 0 None, 1 Public, 2 Private (exclude from discovery). */
const EVENT_PRIVACY_PRIVATE = 2;

export const isPrimaryEventAllowedInDiscovery = ev => {
  if (!ev || typeof ev !== 'object') {
    return false;
  }
  const t = ev.event_privacy_type;
  if (t != null && t !== '') {
    const n = Number(t);
    if (Number.isFinite(n) && n === EVENT_PRIVACY_PRIVATE) {
      return false;
    }
  }
  return !primaryEventHasEnded(ev);
};

const primaryPriceMajorForFilter = ev => {
  const raw = ev.min_price != null ? ev.min_price : ev.max_price;
  if (raw == null || raw === '') {
    return NaN;
  }
  const n = Number.parseFloat(String(raw).replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

/** Keep aligned with SearchPage.duck `RESULT_PAGE_SIZE` (Sharetribe listings per page). */
export const DISCOVERY_SEARCH_LISTINGS_PER_PAGE = 24;

/**
 * Filter primary events to those whose lat/lng falls within the given SDK bounds.
 * Returns the full array when bounds are absent (no location search).
 */
export const filterPrimaryEventsByBounds = (events, bounds) => {
  if (!bounds || !bounds.ne || !bounds.sw) {
    return events || [];
  }
  const neLat = Number(bounds.ne.lat);
  const neLng = Number(bounds.ne.lng);
  const swLat = Number(bounds.sw.lat);
  const swLng = Number(bounds.sw.lng);
  if (
    !Number.isFinite(neLat) ||
    !Number.isFinite(neLng) ||
    !Number.isFinite(swLat) ||
    !Number.isFinite(swLng)
  ) {
    return events || [];
  }
  const minLat = Math.min(swLat, neLat);
  const maxLat = Math.max(swLat, neLat);
  const minLng = Math.min(swLng, neLng);
  const maxLng = Math.max(swLng, neLng);
  return (events || []).filter(ev => {
    const lat = Number(ev.latitude);
    const lng = Number(ev.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return false;
    }
    return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
  });
};

/**
 * Filter primary events whose title (or address) matches a keyword query string.
 * Uses a simple case-insensitive "every token must appear" approach,
 * matching Sharetribe's full-text keyword behavior for listings.
 */
export const filterPrimaryEventsByKeywords = (events, keywordsRaw) => {
  if (!keywordsRaw || typeof keywordsRaw !== 'string' || !keywordsRaw.trim()) {
    return events || [];
  }
  const tokens = keywordsRaw
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) {
    return events || [];
  }
  return (events || []).filter(ev => {
    const haystack = [
      ev.event_title,
      ev.address,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return tokens.every(t => haystack.includes(t));
  });
};

/**
 * Client-side filter for PromoTix primary rows using the same URL params as Sharetribe
 * listing search (category enum → `event_type_id`, `price` as "min,max" major units).
 */
export const filterPrimaryEventsByDiscoveryFilters = (events, selectedFilters = {}) => {
  const list = Array.isArray(events) ? events : [];
  if (!list.length) {
    return [];
  }
  let out = list.filter(isPrimaryEventAllowedInDiscovery);
  const sf = selectedFilters && typeof selectedFilters === 'object' ? selectedFilters : {};

  const eventDatesStr = sf[EVENT_DATES_QUERY_PARAM];
  if (eventDatesStr != null && String(eventDatesStr).trim() !== '') {
    out = out.filter(ev => primaryEventMatchesEventDatesFilter(ev, eventDatesStr));
  }

  const priceStr = sf.price;
  if (priceStr != null && String(priceStr).trim() !== '') {
    const parts = String(priceStr).split(',');
    const minP = parseInt(parts[0], 10);
    const maxP = parseInt(parts[1], 10);
    if (Number.isFinite(minP) && Number.isFinite(maxP)) {
      out = out.filter(ev => {
        const p = primaryPriceMajorForFilter(ev);
        return Number.isFinite(p) && p >= minP && p <= maxP;
      });
    }
  }

  const allowedTypeIds = new Set();
  for (const [key, val] of Object.entries(sf)) {
    if (!key.startsWith('pub_') || val == null || val === '') {
      continue;
    }
    const options = parseSelectFilterOptions(String(val));
    for (const opt of options) {
      const trimmed = String(opt).trim();
      const mapped = SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID[trimmed];
      if (mapped != null && Number.isFinite(Number(mapped))) {
        allowedTypeIds.add(Number(mapped));
      }
    }
  }
  if (allowedTypeIds.size > 0) {
    out = out.filter(ev => {
      const et = Number(ev.event_type_id);
      return Number.isFinite(et) && allowedTypeIds.has(et);
    });
  }

  return out;
};

/**
 * Primary cards for the list panel: one "page" of primaries per Search URL `page`,
 * so primaries advance with pagination instead of repeating the full set after Flex rows.
 */
export const getPrimaryEventsPanelPageSlice = ({
  primaryEvents,
  locationSearch,
  sortParam,
  relevanceKey,
  eventDateSortKey,
  defaultSortKey,
  perPage = DISCOVERY_SEARCH_LISTINGS_PER_PAGE,
}) => {
  const q = parse(locationSearch, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const pageRaw = q.page != null && q.page !== '' ? String(q.page) : '1';
  const requestedPage = Math.max(1, parseInt(pageRaw, 10) || 1);
  const sorted = sortPrimaryEventsForSearch(primaryEvents || [], sortParam, {
    relevanceKey,
    eventDateSortKey,
    defaultSortKey,
    searchParams: q,
  });
  const n = sorted.length;
  if (n === 0) {
    return [];
  }
  const maxPage = Math.max(1, Math.ceil(n / perPage));
  const page = Math.min(requestedPage, maxPage);
  const start = (page - 1) * perPage;
  return sorted.slice(start, start + perPage);
};
